import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import useAuth from 'src/hooks/useAuth';
import useDashboardFilters from 'src/hooks/useDashboardFilters';
import {
  canAccessDashboard,
  dashboardIdFromPath,
  dashboardPath,
  getDefaultAuthenticatedRoute,
  ROUTES,
} from 'src/utils';

/**
 * Keeps the open dashboard in the address bar as `/dashboard/:id`.
 * A linked id the organization cannot see shows the no-access notice.
 */
export default function useDashboardLink() {
  const history = useHistory();
  const location = useLocation();
  const { organization } = useAuth();
  const { dashboardId, selectDashboard } = useDashboardFilters();
  const [denied, setDenied] = useState(false);
  const prevPathId = useRef<string | null>(null);
  const prevSelectedId = useRef<string | null>(null);

  const linkedId = dashboardIdFromPath(location.pathname);
  const canAccess = canAccessDashboard(organization?.permission);

  useEffect(() => {
    if (!organization) return;

    if (linkedId && !canAccess) {
      setDenied(true);
      return;
    }

    const dashboards = organization.dashboards || [];
    const linkedDashboard = linkedId
      ? dashboards.find((item) => item.id === linkedId)
      : undefined;
    const pathChanged = linkedId !== prevPathId.current;
    const selectionChanged = dashboardId !== prevSelectedId.current;

    if (linkedId && !linkedDashboard) {
      const movedOffDeletedDashboard =
        !pathChanged &&
        selectionChanged &&
        !!dashboardId &&
        dashboardId !== linkedId &&
        dashboards.some((item) => item.id === dashboardId);

      if (movedOffDeletedDashboard && dashboardId) {
        prevPathId.current = dashboardId;
        prevSelectedId.current = dashboardId;
        history.replace(dashboardPath(dashboardId));
        return;
      }

      prevPathId.current = linkedId;
      setDenied(true);
      return;
    }

    if (
      pathChanged &&
      selectionChanged &&
      linkedId &&
      linkedId === dashboardId
    ) {
      prevPathId.current = linkedId;
      prevSelectedId.current = dashboardId;
      setDenied(false);
      return;
    }

    if (linkedId && linkedDashboard && pathChanged && !selectionChanged) {
      prevPathId.current = linkedId;
      setDenied(false);
      if (dashboardId !== linkedId) {
        selectDashboard(linkedDashboard);
        prevSelectedId.current = linkedId;
      }
      return;
    }

    if (!linkedId && pathChanged && location.pathname === ROUTES.DASHBOARD) {
      prevPathId.current = null;
      setDenied(false);
      if (dashboardId && !selectionChanged) {
        prevSelectedId.current = null;
        selectDashboard(null);
      } else {
        prevSelectedId.current = dashboardId;
      }
      return;
    }

    if (dashboardId && selectionChanged) {
      prevSelectedId.current = dashboardId;
      const selectedExists = dashboards.some((item) => item.id === dashboardId);
      if (
        selectedExists &&
        linkedId !== dashboardId &&
        location.pathname.startsWith(ROUTES.DASHBOARD)
      ) {
        prevPathId.current = dashboardId;
        history.push(dashboardPath(dashboardId));
      }
      return;
    }
  }, [
    organization,
    linkedId,
    dashboardId,
    canAccess,
    selectDashboard,
    history,
    location.pathname,
  ]);

  const redirectTo = canAccess
    ? ROUTES.DASHBOARD
    : getDefaultAuthenticatedRoute(organization?.permission);

  return {
    denied: denied && !!linkedId,
    redirectTo,
  };
}
